import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Download, FileText } from "lucide-react";
import { LazyTurnstile } from "@/components/site/LazyTurnstile";
import { PageSection } from "@/components/site/PageSection";
import { trackEvent } from "@/lib/analytics";
import { SPANISH_GUIDE_FILENAME, SPANISH_GUIDE_PATH } from "@/lib/es-guide";
import { requestSpanishGuide } from "@/lib/inquiries.functions";

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

/**
 * Spanish lead magnet shown on every /es page: the visitor gives a name and
 * email and unlocks the downloadable PDF guide. Rendered once from
 * EsPageShell so a change here updates the whole Spanish section.
 */
export function EsLeadMagnet() {
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const submitGuide = useServerFn(requestSpanishGuide);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!turnstileToken) {
      setError("Por favor complete la verificación de seguridad antes de enviar.");
      return;
    }

    setSending(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const payload = {
      name: String(formData.get("name") || "").trim(),
      email: String(formData.get("email") || "").trim(),
      phone: String(formData.get("phone") || "").trim() || undefined,
      page: typeof window !== "undefined" ? window.location.pathname : undefined,
      turnstileToken,
    };

    try {
      await submitGuide({ data: payload });
      setDone(true);
      trackEvent("es_guide_request", {
        lead_magnet: "guia_cirugia_bariatrica",
        page_path: payload.page,
        language: "es",
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Ocurrió un error. Intente de nuevo o llámenos al (703) 709-9771."
      );
    } finally {
      setSending(false);
    }
  }

  const fieldClass =
    "mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-brand-logo focus:outline-none focus:ring-2 focus:ring-brand-logo/30";

  return (
    <PageSection className="py-6">
      <section
        aria-labelledby="guia-es"
        className="overflow-hidden rounded-2xl border border-border bg-card"
      >
        <div className="grid gap-6 p-6 md:grid-cols-[1.1fr_1fr] md:p-8">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-brand-soft/60 px-3 py-1 text-xs font-medium uppercase tracking-wider text-brand-deep">
              <FileText size={14} aria-hidden="true" />
              Guía gratuita en español
            </p>
            <h2 id="guia-es" className="mt-3 font-serif text-2xl text-brand-deep">
              Guía de Cirugía Bariátrica en el Norte de Virginia
            </h2>
            <p className="mt-2 text-foreground/85">
              Un PDF de cuatro páginas, en español, con lo que necesita saber antes de decidir:
              manga gástrica, bypass gástrico, balón gástrico y medicamentos para bajar de peso,
              los requisitos de peso e IMC, cómo funciona la cobertura del seguro en Virginia y
              qué esperar paso a paso.
            </p>
            <ul className="mt-3 space-y-1 text-sm text-foreground/80">
              <li>· Comparación de las cinco opciones, en una sola tabla</li>
              <li>· Requisitos habituales del seguro y cómo prepararse</li>
              <li>· Preguntas frecuentes respondidas por nuestro consultorio en Herndon</li>
            </ul>
          </div>

          {done ? (
            <div
              role="status"
              className="flex flex-col justify-center rounded-xl bg-brand-soft/50 p-5 text-center"
            >
              <h3 className="font-serif text-xl text-brand-deep">¡Su guía está lista!</h3>
              <p className="mt-2 text-sm text-foreground/85">
                También recibimos su mensaje: si desea, lo llamamos en español para responder sus
                preguntas.
              </p>
              <a
                href={SPANISH_GUIDE_PATH}
                download={SPANISH_GUIDE_FILENAME}
                onClick={() =>
                  trackEvent("es_guide_download", {
                    lead_magnet: "guia_cirugia_bariatrica",
                    language: "es",
                  })
                }
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                <Download size={16} aria-hidden="true" />
                Descargar la Guía (PDF)
              </a>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className="rounded-xl bg-brand-soft/40 p-5"
              aria-describedby="guia-es-privacidad"
            >
              <div className="space-y-3">
                <div>
                  <label htmlFor="guia-nombre" className="text-sm font-medium text-brand-deep">
                    Nombre
                  </label>
                  <input
                    id="guia-nombre"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="guia-correo" className="text-sm font-medium text-brand-deep">
                    Correo electrónico
                  </label>
                  <input
                    id="guia-correo"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="guia-telefono" className="text-sm font-medium text-brand-deep">
                    Teléfono <span className="font-normal text-muted-foreground">(opcional)</span>
                  </label>
                  <input
                    id="guia-telefono"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    className={fieldClass}
                  />
                </div>
              </div>

              <div className="mt-3">
                <LazyTurnstile
                  siteKey={siteKey}
                  triggerRef={formRef}
                  onSuccess={setTurnstileToken}
                  onError={() => setTurnstileToken(null)}
                  onExpire={() => setTurnstileToken(null)}
                />
              </div>

              {error ? (
                <p role="alert" className="mt-2 text-sm text-destructive">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={sending}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
              >
                <Download size={16} aria-hidden="true" />
                {sending ? "Enviando…" : "Recibir la Guía Gratis"}
              </button>

              <p id="guia-es-privacidad" className="mt-3 text-xs text-muted-foreground">
                Usamos sus datos solo para enviarle la guía y responder sus preguntas. No
                compartimos su información.
              </p>
            </form>
          )}
        </div>
      </section>
    </PageSection>
  );
}
